import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL, MEETING_BOOKINGS_SHEET_URL } from "./constants/adminConstants";
import "./css/adminStyles.css";

// Maps contact detail field keys to DB column names
const CONTACT_TO_COLUMN = {
  name: "name",
  email: "email",
  phone: "phone",
  country: "country",
  note: "notes",
};

const KNOWN_CONTACT_COLUMNS = new Set(["name", "email", "phone", "country", "notes"]);

const trim = (s) => (s || "").trim();

const getContactValue = (meeting, contactDetail) => {
  const trimmedKey = trim(contactDetail.key);
  const colName = CONTACT_TO_COLUMN[trimmedKey] || trimmedKey;
  // 1. Check mapped DB column
  if (KNOWN_CONTACT_COLUMNS.has(colName) && meeting[colName] != null) {
    return meeting[colName];
  }
  // 2. Check form_data with the key
  const formData = meeting.form_data || {};
  if (formData[trimmedKey] != null) return formData[trimmedKey];
  if (contactDetail.key !== trimmedKey && formData[contactDetail.key] != null) return formData[contactDetail.key];
  // 3. Try the computed column name anyway
  if (meeting[colName] != null) return meeting[colName];
  // 4. Fallback for renamed core field keys: check all known columns
  if (!KNOWN_CONTACT_COLUMNS.has(colName)) {
    for (const knownCol of KNOWN_CONTACT_COLUMNS) {
      if (meeting[knownCol] != null) return meeting[knownCol];
    }
  }
  return null;
};

// ── Scrollable table wrapper with left/right scroll buttons ────────────

function TableWithScrollButtons({ children }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap">
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

const MEETINGS_API = API_BASE_URL.replace("/tvc", "");

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "scheduled", label: "Scheduled" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No Show" },
];

const STATUS_STYLES = {
  scheduled: { bg: "#dbeafe", color: "#1e40af" },
  confirmed: { bg: "#d1fae5", color: "#065f46" },
  completed: { bg: "#e0e7ff", color: "#3730a3" },
  cancelled: { bg: "#fee2e2", color: "#991b1b" },
  no_show: { bg: "#f3f4f6", color: "#374151" },
};

function AdminMeetingBookings({ user, onBack }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortOrder, setSortOrder] = useState("newest");
  const perPage = 20;

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const handleOpenSheet = () => {
    window.open(MEETING_BOOKINGS_SHEET_URL, "_blank", "noopener,noreferrer");
  };

  const [pushingToSheet, setPushingToSheet] = useState(false);

  const handlePushToSheet = async () => {
    setPushingToSheet(true);
    setActionMsg("");
    try {
      const res = await fetch(`${MEETINGS_API}/meetings/admin/push-to-sheet`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg("Meeting bookings pushed to Google Sheet successfully.");
      } else {
        setActionMsg(data.error || "Failed to push to sheet.");
      }
    } catch (err) {
      setActionMsg("Network error pushing to sheet.");
    } finally {
      setPushingToSheet(false);
    }
  };

  // Modal for viewing full meeting details
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  // Modal for viewing full notes
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalText, setNotesModalText] = useState("");

  // Modal for setting Google Meet link
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkMeetingData, setLinkMeetingData] = useState(null);
  const [newMeetLink, setNewMeetLink] = useState("");

  // Modal for sending confirmation email
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailMeetingId, setEmailMeetingId] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const token = sessionStorage.getItem("tvc_token");

  // ── Fetch Schedule Meeting contact details for dynamic columns ──
  const [contactDetails, setContactDetails] = useState([]);
  const fetchContactDetails = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/content/scheduleMeeting`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section?.content?.contactDetails) {
        setContactDetails(data.section.content.contactDetails);
      }
    } catch (err) {
      // Silently fail — contact details not available
    }
  }, [token]);

  // All contact detail columns in editor-defined order (including name/email/phone)
  const allContactColumns = contactDetails;

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      params.set("page", page);
      params.set("perPage", perPage);

      const res = await fetch(`${MEETINGS_API}/meetings/admin/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setMeetings(data.data || []);
        const meta = data.meta || {};
        setTotalPages(meta.totalPages || 1);
        setTotalItems(meta.total || 0);
      } else {
        setActionMsg("Failed to load meetings.");
      }
    } catch (err) {
      setActionMsg("Network error loading meetings.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery, page, token]);

  useEffect(() => { fetchContactDetails(); fetchMeetings(); }, [fetchMeetings, fetchContactDetails]);

  const formatDate = (d) => {
    if (!d) return "";
    const dateStr = typeof d === "string" ? d.split("T")[0].split(" ")[0] : "";
    if (!dateStr) return "";
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  };

  const formatSlot = (s) => {
    if (!s) return "";
    const m = String(s).match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return s;
    let h = +m[1];
    const ampm = h >= 12 ? "PM" : "AM";
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return h + ":" + m[2] + " " + ampm;
  };

  const getStatusStyle = (status) => {
    const s = STATUS_STYLES[status] || { bg: "#f1f5f9", color: "#475569" };
    return { backgroundColor: s.bg, color: s.color, padding: "2px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, display: "inline-block" };
  };

  // ── Google Meet Link Modal ──────────────────────────────
  const openLinkModal = (meeting) => {
    setLinkMeetingData(meeting);
    setNewMeetLink(meeting.google_meet_link || "");
    setShowLinkModal(true);
  };

  const handleSaveMeetLink = async () => {
    if (!linkMeetingData?.id) return;
    setActionMsg("");
    try {
      let link = newMeetLink.trim();
      if (link && !/^https?:\/\//i.test(link)) link = "https://" + link;
      const res = await fetch(`${MEETINGS_API}/meetings/admin/${linkMeetingData.id}/meet-link`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ link }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg("Meeting link updated successfully.");
        setShowLinkModal(false);
        fetchMeetings();
      } else {
        setActionMsg(data.message || data.error || "Failed to update link.");
      }
    } catch (err) {
      setActionMsg("Network error updating link.");
    }
  };

  // ── Resend Confirmation Email ───────────────────────────
  const openEmailModal = (meetingId) => {
    setEmailMeetingId(meetingId);
    setShowEmailModal(true);
  };

  const handleResendEmail = async () => {
    if (!emailMeetingId) return;
    setEmailSending(true);
    setActionMsg("");
    try {
      const res = await fetch(`${MEETINGS_API}/meetings/admin/${emailMeetingId}/resend-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg(`Confirmation email resent to customer (${data.data?.email || "unknown"}) and admin.`);
        setShowEmailModal(false);
      } else {
        setActionMsg(data.error || "Failed to send email.");
      }
    } catch (err) {
      setActionMsg("Network error sending email.");
    } finally {
      setEmailSending(false);
    }
  };

  // ── Status Update ───────────────────────────────────────
  const handleStatusChange = async (id, newStatus) => {
    setActionMsg("");
    try {
      const res = await fetch(`${MEETINGS_API}/meetings/admin/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg(`Status updated to "${newStatus}".`);
        fetchMeetings();
      } else {
        setActionMsg(data.error || "Failed to update status.");
      }
    } catch (err) {
      setActionMsg("Network error updating status.");
    }
  };

  // ── Delete ──────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this meeting permanently?")) return;
    setActionMsg("");
    try {
      const res = await fetch(`${MEETINGS_API}/meetings/admin/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg("Meeting deleted.");
        fetchMeetings();
      } else {
        setActionMsg(data.error || "Failed to delete.");
      }
    } catch (err) {
      setActionMsg("Network error deleting.");
    }
  };

  return (
    <div>
      <div className="AdminCard">
        <div className="AdminCardHeader">
           <h3 className="AdminFooterH3" style={{ marginRight: "auto" }}>Booking <span className="AdminCountBadge">{meetings.length}</span></h3>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
              Google Sheet
            </button>
            <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={pushingToSheet} title="Push to Sheet">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="AdminFilterBar" style={{ flexWrap: "nowrap", marginBottom: "24px", justifyContent: "flex-end", alignItems: "center" }}>
          <span style={{ marginRight: "auto", fontWeight: 600, fontSize: 15, color: "#1B2A4A" }}>Booking</span>
          <div className={`AdminPillSearchWrap ${searchOpen ? "is-open" : ""}`} style={{ flexShrink: 0 }}>
            <span className="AdminPillSearchIcon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              ref={searchRef}
              type="text"
              className="AdminPillSearch"
              placeholder="Search name, email, or booking ID..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
               onBlur={() => { setSearchQuery(""); setPage(1); setTimeout(() => setSearchOpen(false), 200) }}
            />
            {!searchOpen && (
              <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
            )}
          </div>
          <div className="AdminPillFilterItem">
            <select className="AdminPillSelect" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <select className="AdminPillSelect" style={{ width: 100 }} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>

        {actionMsg && (
          <p className={`AdminMsgBox ${actionMsg.includes("error") || actionMsg.includes("Failed") || actionMsg.includes("fail") ? "AdminError" : "AdminSuccess"}`}>
            {actionMsg}
          </p>
        )}

        {/* Table */}
        {loading ? (
          <div className="AdminEmptyState">Loading meetings...</div>
        ) : meetings.length === 0 ? (
          <div className="AdminEmptyState">No meetings found.</div>
        ) : (
          <TableWithScrollButtons>
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  {allContactColumns.map(cd => (
                    <th key={cd.key}>{cd.label.replace(/\s*\*?$/, '')}</th>
                  ))}
                  <th>Type</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th style={{ minWidth: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...meetings].sort((a, b) => sortOrder === "newest" ? (b.id || 0) - (a.id || 0) : (a.id || 0) - (b.id || 0)).map((m) => {
                  return (
                  <tr key={m.id} onClick={() => { setSelectedMeeting(m); setShowMeetingModal(true); }} style={{
                    cursor: "pointer", background: m.status === "scheduled" ? "#fffbeb" : 
                                m.status === "cancelled" ? "#fef2f2" : 
                                m.status === "completed" ? "#f0fdf4" : "transparent",
                    transition: "filter 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.filter = "brightness(0.97)"}
                  onMouseLeave={(e) => e.currentTarget.style.filter = "none"}
                  >
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{m.human_id}</td>
                    {allContactColumns.map(cd => {
                      let value;
                      if (cd.key === "email") {
                        const emailVal = getContactValue(m, cd);
                        value = emailVal ? <a href={`mailto:${emailVal}`} className="AdminLink">{emailVal}</a> : "—";
                      } else if (cd.key === "note") {
                        const notesVal = getContactValue(m, cd) || "";
                        value = notesVal ? (
                          <span
                            style={{ cursor: "pointer", color: "#2563eb", textDecoration: "underline", textDecorationStyle: "dotted" }}
                            onClick={() => { setNotesModalText(notesVal); setShowNotesModal(true); }}
                          >
                            {notesVal.length > 100 ? notesVal.substring(0, 100) + "..." : notesVal}
                          </span>
                        ) : "—";
                      } else {
                        const val = getContactValue(m, cd);
                        value = val != null && val !== "" ? val : "—";
                      }
                      const tdStyle = {
                        fontSize: 13,
                        fontWeight: cd.key === "name" ? 500 : undefined,
                        whiteSpace: cd.key === "name" ? "nowrap" : undefined,
                        ...(cd.key === "note" ? { whiteSpace: "normal", wordBreak: "break-word", minWidth: 200, maxWidth: 300 } : {}),
                      };
                      return (
                        <td key={cd.key} style={tdStyle}>
                          {value}
                        </td>
                      );
                    })}
                    <td style={{ textTransform: "capitalize", fontSize: 13 }}>{m.meeting_type?.replace(/-/g, " ")}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>{formatDate(m.meeting_date)}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>{formatSlot(m.meeting_slot)}</td>
                    <td style={{ fontSize: 13 }}>{m.duration_minutes} min</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span style={getStatusStyle(m.status)}>{m.status}</span>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 100 }}>
                        {/* Free vs Paid indicator */}
                        <span style={{
                          padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600,
                          display: "inline-block", alignSelf: "flex-start",
                          backgroundColor: m.total_paid === 0 ? "#ede9fe" : "#dbeafe",
                          color: m.total_paid === 0 ? "#5b21b6" : "#1e40af",
                        }}>
                          {m.total_paid === 0 ? "Free Call" : "Paid"}
                        </span>
                        {/* Payment status badge */}
                        <span style={{
                          padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 500,
                          display: "inline-block", alignSelf: "flex-start",
                          backgroundColor: m.payment_status === "paid" ? "#d1fae5" : 
                                           m.payment_status === "pending" ? "#fef3c7" : "#f3f4f6",
                          color: m.payment_status === "paid" ? "#065f46" : 
                                 m.payment_status === "pending" ? "#92400e" : "#374151",
                        }}>
                          {m.payment_status || "pending"}
                        </span>
                        {/* Payment breakdown */}
                        {m.total_paid > 0 && (
                          <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.6 }}>
                            {m.consultation_fee > 0 && <div>Fee: ₹{m.consultation_fee}</div>}
                            {m.conv_fee > 0 && <div>Conv: ₹{m.conv_fee}</div>}
                            {m.gst_amount > 0 && <div>GST: ₹{m.gst_amount}</div>}
                            <div style={{ fontWeight: 600, color: "#1e293b", borderTop: "1px solid #e2e8f0", marginTop: 2, paddingTop: 2 }}>
                              Total: ₹{m.total_paid}
                            </div>
                          </div>
                        )}
                        {/* Payment method */}
                        {m.payment_method && (
                          <span style={{
                            fontSize: 11, color: "#64748b",
                            fontStyle: "italic",
                          }}>
                            via {m.payment_method.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexDirection: "column", alignItems: "flex-start" }}>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {m.status === "scheduled" && (
                            <button title="Confirm"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#16a34a", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => handleStatusChange(m.id, "confirmed")}>
                              &#10003;
                            </button>
                          )}
                          {(m.status === "scheduled" || m.status === "confirmed") && (
                            <button title="Mark Completed"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#2563eb", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => handleStatusChange(m.id, "completed")}>
                              &#10004;
                            </button>
                          )}
                          {(m.status === "scheduled" || m.status === "confirmed") && (
                            <button title="No Show"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => handleStatusChange(m.id, "no_show")}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                                <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                              </svg>
                            </button>
                          )}
                          {m.status !== "cancelled" && m.status !== "no_show" && m.status !== "completed" && (
                            <button title="Cancel"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(m.id, "cancelled"); }}>
                              &#10005;
                            </button>
                          )}
                          <button title="Meeting Link"
                            style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#7c3aed", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                            onClick={(e) => { e.stopPropagation(); openLinkModal(m); }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                            </svg>
                          </button>
                          {m.status === "scheduled" || m.status === "confirmed" ? (
                            <button title="Send Meet Link Email"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#2563eb", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={(e) => { e.stopPropagation(); openEmailModal(m.id); }}>
                              &#9993;
                            </button>
                          ) : (
                            <button title="Delete"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}>
                              &#128465;
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
              </tbody>
            </table>
          </TableWithScrollButtons>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="AdminPagination">
            <button
              className="AdminPageBtn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              &laquo; Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (page <= 4) {
                pageNum = i + 1;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`AdminPageBtn ${page === pageNum ? "AdminPageBtnActive" : ""}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="AdminPageBtn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next &raquo;
            </button>
          </div>
        )}
      </div>

      {/* ── Set Meeting Link Modal ── */}
      {showLinkModal && (
        <div className="AdminModal" onClick={() => setShowLinkModal(false)} style={{ zIndex: 1001 }}>
          <div className="AdminModalContent AdminModalMaxW480" onClick={(e) => e.stopPropagation()} style={{ border: "1px solid #cbd5e1" }}>
            <h3 className="AdminModalTitle">Set Google Meet Link</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              Update the meeting link for <strong>{linkMeetingData?.name || linkMeetingData?.human_id}</strong>
            </p>
            {/* Google Calendar button */}
            {linkMeetingData && (() => {
              const datePart = String(linkMeetingData.meeting_date || "").split("T")[0].replace(/-/g, "");
              const slotPart = String(linkMeetingData.meeting_slot || "09:00").replace(":", "");
              const startStr = `${datePart}T${slotPart}00`;
              const dur = parseInt(linkMeetingData.duration_minutes, 10) || 15;
              const endH = Math.floor((parseInt(slotPart.slice(0,2), 10) * 60 + parseInt(slotPart.slice(2), 10) + dur) / 60);
              const endM = (parseInt(slotPart.slice(0,2), 10) * 60 + parseInt(slotPart.slice(2), 10) + dur) % 60;
              const endStr = `${datePart}T${String(endH).padStart(2,"0")}${String(endM).padStart(2,"0")}00`;
              const calUrl = "https://calendar.google.com/calendar/render?action=TEMPLATE"
                + "&text=" + encodeURIComponent(`Meeting with ${linkMeetingData.name || "Customer"} - ${(linkMeetingData.meeting_type || "").replace(/-/g, " ")}`)
                + "&dates=" + startStr + "/" + endStr
                + "&ctz=Asia/Kolkata"
                + "&details=" + encodeURIComponent(
                    `Booking ID: ${linkMeetingData.human_id || ""}\n`
                    + `Customer: ${linkMeetingData.name || ""}\n`
                    + `Email: ${linkMeetingData.email || ""}\n`
                    + `Phone: ${linkMeetingData.phone_code || ""} ${linkMeetingData.phone || ""}\n`
                    + `Meeting Type: ${(linkMeetingData.meeting_type || "").replace(/-/g, " ")}\n`
                    + `Duration: ${linkMeetingData.duration_minutes || ""} minutes\n`
                    + `Meeting Link: ${newMeetLink || "(not set)"}`
                  );
              return (
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <a
                    href={calUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "8px 16px", fontSize: 13, fontWeight: 600,
                      background: "#1a56db", color: "#fff", borderRadius: 8,
                      textDecoration: "none",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    Google Calendar
                  </a>
                </div>
              );
            })()}
            <input
              className="AdminInput"
              style={{ width: "100%", marginBottom: 16 }}
              type="url"
              placeholder="https://meet.google.com/abc-defg-hij"
              value={newMeetLink}
              onChange={(e) => setNewMeetLink(e.target.value)}
            />
            <div className="AdminFlexCenterG12">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowLinkModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall" onClick={handleSaveMeetLink} disabled={!newMeetLink.trim()}>
                Save Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Resend Email Confirmation Modal ── */}
      {showEmailModal && (
        <div className="AdminModal" onClick={() => setShowEmailModal(false)}>
          <div className="AdminModalContent AdminModalMaxW400" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 8 }}>{"\u2709\uFE0F"}</div>
            <h3 className="AdminModalTitle AdminTaCenter">Send Meet Link Email</h3>
            <p className="AdminText13 AdminTextMuted AdminTaCenter AdminM0B20">
              Send the Google Meet link email to the customer?
            </p>
            <div className="AdminFlexCenterG12">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowEmailModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall" onClick={handleResendEmail} disabled={emailSending}>
                {emailSending ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Meeting Details Modal ── */}
      {showMeetingModal && selectedMeeting && (
        <div className="AdminModal" onClick={() => setShowMeetingModal(false)}>
          <div className="AdminModalContent" style={{ maxWidth: 640, padding: "24px 28px", maxHeight: "85vh", overflowY: "auto", position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowMeetingModal(false)}
              style={{ position: "absolute", top: 12, right: 16, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#64748b", lineHeight: 1 }}
              title="Close"
            >
              &times;
            </button>
            <h3 className="AdminModalTitle" style={{ marginBottom: 18 }}>
              Meeting Details
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openLinkModal(selectedMeeting);
                  setShowMeetingModal(false);
                }}
                style={{
                  marginLeft: 12, padding: "4px 12px", fontSize: 12, fontWeight: 600,
                  background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6,
                  cursor: "pointer", verticalAlign: "middle",
                }}
                title={selectedMeeting.google_meet_link ? "Open Google Meet Link" : "Set Google Meet Link"}
              >
                Link
              </button>
            </h3>

            {/* Contact details section */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
              {allContactColumns.map(cd => {
                let value;
                if (cd.key === "email") {
                  const emailVal = getContactValue(selectedMeeting, cd);
                  value = emailVal ? <a href={`mailto:${emailVal}`} className="AdminLink">{emailVal}</a> : "—";
                } else {
                  const val = getContactValue(selectedMeeting, cd);
                  value = val != null && val !== "" ? val : "—";
                }
                const isLong = cd.key === "note" || (typeof value === "string" && value.length > 80);
                return (
                  <div key={cd.key} style={{ gridColumn: isLong ? "1 / -1" : undefined, borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>{cd.label}</label>
                    <div style={{ fontSize: 14, color: "#1e293b", whiteSpace: isLong ? "pre-wrap" : undefined, wordBreak: "break-word" }}>{value}</div>
                  </div>
                );
              })}
            </div>

            {/* Orphaned form_data entries that don't match current contact detail keys */}
            {(() => {
              const currentKeys = new Set(allContactColumns.map(cd => cd.key));
              const orphaned = Object.entries(selectedMeeting.form_data || {}).filter(([k]) => !currentKeys.has(k));
              if (orphaned.length === 0) return null;
              return (
                <>
                  <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>Additional Data</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
                    {orphaned.map(([key, val]) => (
                      <div key={key} style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>{key}</label>
                        <div style={{ fontSize: 14, color: "#1e293b", wordBreak: "break-word" }}>{val != null && val !== "" ? val : "—"}</div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}

            {/* Meeting info section */}
            <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Booking ID</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{selectedMeeting.human_id}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Meeting Type</label>
                <div style={{ fontSize: 14, color: "#1e293b", textTransform: "capitalize" }}>{selectedMeeting.meeting_type?.replace(/-/g, " ") || "—"}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Date</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{formatDate(selectedMeeting.meeting_date)}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Time</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{formatSlot(selectedMeeting.meeting_slot)}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Duration</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{selectedMeeting.duration_minutes ? `${selectedMeeting.duration_minutes} min` : "—"}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Status</label>
                <div>
                  <span className="AdminActionBadge" style={getStatusStyle(selectedMeeting.status)}>{selectedMeeting.status}</span>
                </div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Payment Status</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{selectedMeeting.payment_status || "—"}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Total Paid</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>
                  {selectedMeeting.total_paid === 0 ? (
                    <span style={{ color: "#5b21b6", fontWeight: 600 }}>Free</span>
                  ) : selectedMeeting.total_paid ? `$${selectedMeeting.total_paid}` : "—"}
                </div>
              </div>
            </div>

            {/* Actions */}
            <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>Actions</h4>
            <div style={{ textAlign: "center" }}>
              {selectedMeeting.status === "scheduled" && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#d1fae5", color: "#065f46", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={async () => {
                    await handleStatusChange(selectedMeeting.id, "confirmed");
                    setShowMeetingModal(false);
                    openLinkModal(selectedMeeting);
                  }}>
                  Confirm
                </button>
              )}
              {(selectedMeeting.status === "scheduled" || selectedMeeting.status === "confirmed") && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#e0e7ff", color: "#3730a3", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={() => { handleStatusChange(selectedMeeting.id, "completed"); setShowMeetingModal(false); }}>
                  Complete
                </button>
              )}
              {(selectedMeeting.status === "scheduled" || selectedMeeting.status === "confirmed") && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#f3f4f6", color: "#374151", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={() => { handleStatusChange(selectedMeeting.id, "no_show"); setShowMeetingModal(false); }}>
                  No Show
                </button>
              )}
              {selectedMeeting.status !== "cancelled" && selectedMeeting.status !== "no_show" && selectedMeeting.status !== "completed" && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#fee2e2", color: "#991b1b", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={(e) => { e.stopPropagation(); handleStatusChange(selectedMeeting.id, "cancelled"); setShowMeetingModal(false); }}>
                  Cancel
                </button>
              )}
              {selectedMeeting.status === "scheduled" || selectedMeeting.status === "confirmed" ? (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#dbeafe", color: "#1e40af", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={(e) => { e.stopPropagation(); setShowMeetingModal(false); openEmailModal(selectedMeeting.id); }}>
                  Send Meet Link
                </button>
              ) : (
                <button className="AdminButton AdminButtonSmall AdminButtonDanger" style={{ display: "inline-flex", margin: 4 }}
                  onClick={(e) => { e.stopPropagation(); setShowMeetingModal(false); handleDelete(selectedMeeting.id); }}>
                  Delete
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── View Full Notes Modal ── */}
      {showNotesModal && (
        <div className="AdminModal" onClick={() => setShowNotesModal(false)}>
          <div className="AdminModalContent AdminModalMaxW520" onClick={(e) => e.stopPropagation()}>
            <h3 className="AdminModalTitle">Additional Notes</h3>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 400, overflowY: "auto" }}>
              {notesModalText}
            </div>
            <div className="AdminFlexCenter" style={{ marginTop: 20 }}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowNotesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMeetingBookings;
